/* Cart-page adapter for the gift app's existing runtime projection. */
(function (scope) {
  'use strict';
  const core = scope?.KateCartGiftCore || (typeof module !== 'undefined' && module.exports ? require('./cart-gift-core.js') : null);
  if (typeof module !== 'undefined' && module.exports) module.exports = core;
  if (!scope?.document || !core) return;
  const { ATTR, DEFERRED, suffix, managed, external, fingerprint, selectedLines, matches, amount, resolve, properties, giftState } = core;
  const normal = value => String(value || '').trim().toLowerCase();
  const price = line => Math.max(0, Number(line.final_line_price ?? line.line_price ?? 0));
  const escape = value => String(value ?? '').replace(/[&<>"']/g, char => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char]);
  const rewardLine = (config, line, reward) => String(line.variant_id) === suffix(reward.candidate.variantId) &&
    Object.entries(properties(config, reward)).every(([key, value]) => line.properties?.[key] === value);
  const document = scope.document;
  const mount = document.getElementById('cart-gift-tiers');
  const root = document.getElementById('zyf-gift-promotions-root');
  if (!mount || !root) return;
  // This deferred asset precedes content_for_header and the app's deferred assets.
  // The app's public initialization guards leave the drawer untouched on other pages.
  if (root.dataset.initialized === 'true') {
    mount.hidden = false;
    mount.textContent = 'Free gift setup could not be loaded. Please refresh before checkout.';
    return;
  }
  root.dataset.initialized = 'true';
  root.dataset.giftInfoInitialized = 'true';
  root.dataset.conflictInitialized = 'true';
  const read = (id, fallback, source = document) => {
    try { const value = JSON.parse(source.getElementById(id)?.textContent || 'null'); return (typeof value === 'string' ? JSON.parse(value) : value) || fallback; }
    catch { throw new Error('Unable to read the free gift activity. Please refresh and try again.'); }
  };
  const route = scope.Shopify?.routes?.root || '/';
  const stock = new Map();
  const checking = new Set();
  const choices = new Map();
  const displays = new Map();
  const choiceKey = 'kate-cart-gift-choices-v1';
  let config, facts, cart, gate, busy = false, timer, queue = Promise.resolve(), refreshPending = false;
  let updated = false;
  let lastFresh = 0;
  const serial = task => (queue = queue.then(task, task));
  const message = text => {
    const node = document.getElementById('cart-gift-status');
    if (node) { node.textContent = text; node.hidden = !text; }
  };
  const request = async (path, body, accept = 'application/json') => {
    const controller = new AbortController();
    const timeout = scope.setTimeout(() => controller.abort(), 12000);
    try {
      const response = await fetch(route + path, { credentials: 'same-origin', cache: 'no-store', signal: controller.signal,
        headers: { Accept: accept, ...(body ? { 'Content-Type': 'application/json' } : {}) },
        ...(body ? { method: 'POST', body: JSON.stringify(body) } : {}) });
      let value;
      try { value = await response.json(); }
      catch { const error = new Error('Unable to read the server response. Please try again.'); error.status = response.status; throw error; }
      if (!response.ok) { const error = new Error(value.description || 'Unable to update your cart. Please try again.'); error.status = response.status; throw error; }
      return value;
    } catch (error) {
      if (controller.signal.aborted || error.name === 'AbortError') throw new Error('The request timed out. Please try again.');
      throw error;
    } finally { clearTimeout(timeout); }
  };
  const fetchCart = () => request('cart.js');
  const rate = () => Number(scope.Shopify?.currency?.rate || 1) || 1;
  const allows = rule => gate?.allows(rule) ?? true;
  const saveChoices = () => { try { sessionStorage.setItem(choiceKey, JSON.stringify({ revision: config.revision, values: [...choices] })); } catch {} };
  const productStock = async (handle, candidates) => {
    try {
      if (!handle) throw new Error('Missing gift product');
      // This is a rendered Liquid view, not an authenticated Shopify JSON endpoint.
      const product = await request(`products/${encodeURIComponent(handle)}?view=gift-stock`, undefined, 'text/html');
      if (!Array.isArray(product.variants)) throw new Error('Invalid gift inventory response');
      for (const candidate of candidates) {
        const id = suffix(candidate.variantId);
        const variant = product.variants.find(item => String(item.id) === id);
        if (variant && typeof variant.giftAvailable !== 'boolean') throw new Error('Invalid gift availability');
      }
      for (const candidate of candidates) {
        const id = suffix(candidate.variantId);
        stock.set(id, product.variants.find(item => String(item.id) === id)?.giftAvailable === true);
      }
    } catch (error) { candidates.forEach(candidate => stock.set(suffix(candidate.variantId), error.status === 404 ? false : null)); }
    finally { candidates.forEach(candidate => checking.delete(suffix(candidate.variantId))); render(); }
  };
  const refreshStock = async () => {
    const candidates = new Map((config.rules || []).flatMap(rule => rule.tiers.flatMap(tier => tier.candidates)).map(candidate => [suffix(candidate.variantId), candidate]));
    const products = new Map();
    for (const [id, candidate] of candidates) {
      checking.add(id); stock.set(id, null);
      const handle = candidate.productHandle || '';
      if (!products.has(handle)) products.set(handle, []);
      products.get(handle).push(candidate);
    }
    render();
    await Promise.all([...products].map(([handle, items]) => productStock(handle, items)));
  };
  const money = value => new Intl.NumberFormat(document.documentElement.lang || 'en', {
    style: 'currency', currency: cart?.currency || config.currencyCode || 'USD', maximumFractionDigits: 2
  }).format(value * rate());
  const stockLabel = candidate => checking.has(suffix(candidate?.variantId)) ? 'Checking stock…' : stock.get(suffix(candidate?.variantId)) === true ? 'Available' : stock.get(suffix(candidate?.variantId)) === false ? 'Fully Claimed' : 'Stock check failed';
  const detailUrl = candidate => candidate?.productHandle ? `${route}products/${encodeURIComponent(candidate.productHandle)}?variant=${encodeURIComponent(suffix(candidate.variantId))}` : '';
  let dialog, openTier, returnFocus, previousOverflow, historyOpen = false;
  function renderDialog() {
    if (!dialog?.open || !openTier) return;
    const rule = config.rules?.find(item => item.id === openTier.rule);
    const tier = rule?.tiers.find(item => item.id === openTier.tier);
    if (!tier || mount.hidden) { closeDialog(); return; }
    const focus = document.activeElement?.dataset?.giftChoice;
    const key = `${rule.id}:${tier.id}`;
    const reward = resolve(config, cart, facts, stock, choices, rate(), undefined, allows);
    dialog.querySelector('.cart-gift-dialog__items').innerHTML = tier.candidates.map(candidate => {
      const id = suffix(candidate.variantId), available = stock.get(id) === true && !checking.has(id);
      const title = candidate.productTitle || candidate.label || 'Free gift';
      const receiving = reward?.rule.id === rule.id && reward.tier.id === tier.id && suffix(reward.candidate.variantId) === id && available;
      return `<article class="cart-gift-dialog__item${choices.get(key) === id ? ' is-selected' : ''}"><button type="button" data-gift-choice="${escape(id)}" ${!available ? 'disabled' : ''} aria-label="Select ${escape(title)}" aria-pressed="${choices.get(key) === id}">${candidate.imageUrl ? `<img src="${escape(candidate.imageUrl)}" alt="" width="96" height="96">` : '<span aria-hidden="true">🎁</span>'}<span><strong>${escape(title)}</strong>${candidate.variantTitle && normal(candidate.variantTitle) !== 'default title' ? `<small>${escape(candidate.variantTitle)}</small>` : ''}<small>${stockLabel(candidate)}${receiving ? ' · Selected' : ''}</small></span></button>${detailUrl(candidate) ? `<a href="${escape(detailUrl(candidate))}" target="_blank" rel="noopener">View details</a>` : ''}</article>`;
    }).join('');
    dialog.querySelector('[data-gift-retry]').hidden = !tier.candidates.some(candidate => !checking.has(suffix(candidate.variantId)) && stock.get(suffix(candidate.variantId)) == null);
    if (focus) dialog.querySelector(`[data-gift-choice="${scope.CSS.escape(focus)}"]`)?.focus();
  }
  function closeDialog(fromHistory = false) {
    if (!dialog?.open) return;
    dialog.close(); openTier = null;
    document.documentElement.style.overflow = previousOverflow || '';
    const trigger = returnFocus && mount.querySelector(`[data-gift-open="${scope.CSS.escape(returnFocus)}"]`);
    trigger?.focus();
    if (historyOpen && !fromHistory) scope.history.back();
    historyOpen = false;
  }
  function showDialog(trigger) {
    if (!dialog) {
      dialog = document.createElement('dialog'); dialog.className = 'cart-gift-dialog';
      dialog.setAttribute('aria-labelledby', 'cart-gift-dialog-title');
      dialog.innerHTML = '<div class="cart-gift-dialog__panel"><header><h2 id="cart-gift-dialog-title" class="heading h4">Choose your gift</h2><button type="button" data-gift-close aria-label="Close gift selection">×</button></header><div class="cart-gift-dialog__items"></div><button type="button" data-gift-retry hidden>Retry stock check</button></div>';
      document.body.appendChild(dialog);
      dialog.addEventListener('cancel', event => { event.preventDefault(); closeDialog(); });
      dialog.addEventListener('click', event => {
        if (event.target === dialog || event.target.closest('[data-gift-close]')) { closeDialog(); return; }
        if (event.target.closest('[data-gift-retry]')) { retryStock(); return; }
        const button = event.target.closest('[data-gift-choice]');
        if (!button || button.disabled || busy) return;
        const id = button.dataset.giftChoice;
        if (stock.get(id) !== true || checking.has(id)) return;
        const key = `${openTier.rule}:${openTier.tier}`;
        const changed = choices.get(key) !== id;
        choices.set(key, id); displays.set(key, id); render(); closeDialog();
        if (changed) animateSelection(key, 1);
        if (changed) void serial(async () => { cart = await cleanGifts(); render(); }).catch(error => message(error.message));
      });
    }
    const article = trigger.closest('[data-rule]');
    openTier = { rule: article.dataset.rule, tier: article.dataset.tier }; returnFocus = trigger.dataset.giftOpen;
    previousOverflow = document.documentElement.style.overflow;
    dialog.showModal(); document.documentElement.style.overflow = 'hidden';
    scope.history.pushState({ ...scope.history.state, kateGiftDialog: true }, '', scope.location.href); historyOpen = true;
    renderDialog(); dialog.querySelector('[data-gift-close]').focus();
  }
  const retryStock = () => {
    if (busy || refreshPending) return;
    refreshPending = true;
    serial(() => refresh({ fresh: true })).catch(error => message(error.message)).finally(() => { refreshPending = false; });
  };
  function animateSelection(key, direction) {
    const trigger = mount.querySelector(`[data-gift-open="${scope.CSS.escape(key)}"]`);
    const article = trigger?.closest('[data-rule]');
    if (!article || scope.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    const product = article.querySelector('.cart-gift-tier__product');
    const stack = article.querySelector('.cart-gift-tier__stack');
    const target = scope.matchMedia?.('(max-width: 740px)').matches ? stack : product;
    if (!target?.animate) return;
    target.getAnimations().forEach(animation => animation.cancel());
    target.animate([{ opacity: .45, transform: `translateX(${direction * 8}px)` }, { opacity: 1, transform: 'translateX(0)' }], { duration: 220, easing: 'ease-out' });
  }
  scope.addEventListener('popstate', () => { if (dialog?.open) closeDialog(true); });

  let mobilePositioned = false, mobileTouched = false;
  const mobile = () => scope.matchMedia?.('(max-width: 740px)').matches;
  const preserveMobilePosition = event => {
    if (mobile() && event.target.closest?.('.cart-gift-tiers__grid')) mobileTouched = true;
  };
  mount.addEventListener('touchstart', preserveMobilePosition, { passive: true });
  mount.addEventListener('wheel', preserveMobilePosition, { passive: true });
  mount.addEventListener('pointerdown', preserveMobilePosition, { passive: true });
  mount.addEventListener('keydown', preserveMobilePosition);
  let giftGesture = null, suppressGiftClickUntil = 0;
  mount.addEventListener('pointerdown', event => {
    const grid = event.target.closest?.('.cart-gift-tiers__grid');
    if (!mobile() || !grid || event.button !== 0) return;
    giftGesture = { id: event.pointerId, grid, x: event.clientX, y: event.clientY, scroll: grid.scrollLeft, mouse: event.pointerType === 'mouse', dragged: false };
  });
  scope.addEventListener('pointermove', event => {
    const gesture = giftGesture;
    if (!gesture || gesture.id !== event.pointerId) return;
    const dx = event.clientX - gesture.x, dy = event.clientY - gesture.y;
    if (Math.abs(dx) > 8 && Math.abs(dx) > Math.abs(dy)) {
      gesture.dragged = true;
      mobileTouched = true;
      suppressGiftClickUntil = Date.now() + 500;
    }
    // Touch uses native momentum scrolling; mouse dragging supports device previews.
    if (gesture.mouse && gesture.dragged) {
      event.preventDefault();
      gesture.grid.scrollLeft = gesture.scroll - dx;
    }
  }, { passive: false });
  const finishGiftGesture = () => {
    if (giftGesture?.dragged) suppressGiftClickUntil = Date.now() + 500;
    giftGesture = null;
  };
  scope.addEventListener('pointerup', finishGiftGesture);
  scope.addEventListener('pointercancel', finishGiftGesture);
  mount.addEventListener('dragstart', event => {
    if (mobile() && event.target.closest?.('.cart-gift-tiers__grid')) event.preventDefault();
  });
  function render() {
    const previousScroll = mount.querySelector('.cart-gift-tiers__grid')?.scrollLeft || 0;
    const rules = (config.rules || []).filter(rule => allows(rule) && rule.tiers.length);
    const active = document.activeElement;
    const focusKey = active?.closest?.('#cart-gift-tiers') ? active.dataset?.focusKey : null;
    mount.hidden = !rules.length || !cart.items.some(line => !managed(line));
    if (mount.hidden) { mount.innerHTML = ''; if (dialog?.open) closeDialog(); return; }
    const reward = resolve(config, cart, facts, stock, choices, rate(), undefined, allows);
    mount.innerHTML = `<h2 class="heading h4">Your free gift</h2><p class="cart-gift-tiers__intro">Only the highest eligible available tier applies. Your gift will be added when you checkout.</p><div class="cart-gift-tiers__grid">${rules.map(rule => {
      const subtotal = amount(rule, cart, facts, rate());
      return [...rule.tiers].sort((a, b) => a.threshold - b.threshold).map(tier => {
        const key = `${rule.id}:${tier.id}`;
        const candidates = tier.candidates;
        const available = candidates.filter(candidate => stock.get(suffix(candidate.variantId)) === true);
        const soldOut = !available.length && candidates.every(candidate => stock.get(suffix(candidate.variantId)) === false);
        const met = subtotal >= Number(tier.threshold);
        const selected = available.find(candidate => suffix(candidate.variantId) === choices.get(key)) || available[0];
        const previousChoice = choices.get(key);
        if (selected && !candidates.some(item => checking.has(suffix(item.variantId))) &&
          (!choices.has(key) || stock.get(choices.get(key)) != null)) choices.set(key, suffix(selected.variantId));
        if (selected && (!previousChoice || previousChoice !== choices.get(key)) && (displays.get(key) === previousChoice || stock.get(displays.get(key)) === false)) displays.set(key, suffix(selected.variantId));
        const candidate = candidates.find(item => suffix(item.variantId) === displays.get(key)) || selected || candidates[0];
        if (candidate) displays.set(key, suffix(candidate.variantId));
        const availableCandidate = candidate && stock.get(suffix(candidate.variantId)) === true;
        const receiving = reward?.rule.id === rule.id && reward.tier.id === tier.id;
        const isSelected = receiving && availableCandidate && suffix(reward.candidate.variantId) === suffix(candidate?.variantId);
        const title = candidate?.productTitle || candidate?.label || 'Free gift';
        const href = candidate?.productHandle ? `${route}products/${encodeURIComponent(candidate.productHandle)}?variant=${encodeURIComponent(suffix(candidate.variantId))}` : '';
        const pending = candidates.some(item => checking.has(suffix(item.variantId)));
        const failed = candidates.some(item => !checking.has(suffix(item.variantId)) && stock.get(suffix(item.variantId)) == null);
        const badge = soldOut ? 'Fully Claimed' : !available.length ? (pending ? 'Checking availability' : 'Stock check failed') : met ? '' : 'Not yet qualified';
        const mobileBadge = soldOut ? 'Fully Claimed' : !met ? `${escape(money(Math.max(0, tier.threshold - subtotal)))} AWAY` : badge;
        const content = `${candidate?.imageUrl ? `<img src="${escape(candidate.imageUrl)}" alt="${escape(title)}" loading="lazy" width="96" height="96">` : '<span class="cart-gift-tier__placeholder" aria-hidden="true">🎁</span>'}<span class="cart-gift-tier__text"><span class="cart-gift-tier__title">${escape(title)}</span>${candidate?.variantTitle && normal(candidate.variantTitle) !== 'default title' ? `<small>${escape(candidate.variantTitle)}</small>` : ''}</span>`;
        const stackFirst = selected || candidate;
        const stack = [stackFirst, ...candidates.filter(item => item !== stackFirst)].filter(Boolean).slice(0, 3);
        return `<article class="cart-gift-tier${receiving && availableCandidate && !soldOut ? ' is-qualified' : ''}${receiving ? ' is-receiving' : ''}" data-rule="${escape(rule.id)}" data-tier="${escape(tier.id)}">
          <div class="cart-gift-tier__header"><strong>Spend ${escape(money(tier.threshold))}</strong>${badge ? `<span class="cart-gift-tier__badge cart-gift-tier__badge--desktop${soldOut ? ' is-claimed' : ''}">${badge}</span>` : ''}${mobileBadge ? `<span class="cart-gift-tier__badge cart-gift-tier__badge--mobile${soldOut ? ' is-claimed' : !met ? ' is-away' : ''}">${mobileBadge}</span>` : ''}</div>
          <div class="cart-gift-tier__carousel"><button type="button" data-direction="-1" data-focus-key="${escape(key)}:previous" aria-label="Previous gift" ${candidates.length < 2 ? 'hidden' : ''}>‹</button>
          ${href ? `<a class="cart-gift-tier__product${isSelected ? ' is-selected' : ''}" href="${escape(href)}" target="_blank" rel="noopener" aria-label="View ${escape(title)} in a new tab">${content}</a>` : `<div class="cart-gift-tier__product${isSelected ? ' is-selected' : ''}">${content}</div>`}
          <button type="button" data-direction="1" data-focus-key="${escape(key)}:next" aria-label="Next gift" ${candidates.length < 2 ? 'hidden' : ''}>›</button></div>
          <button class="cart-gift-tier__stack" type="button" data-gift-open="${escape(key)}" aria-label="${candidates.length > 1 ? `Choose from ${candidates.length} gifts` : 'View gift'}"><span class="cart-gift-tier__stack-images">${stack.map((item, index) => `<span style="--gift-depth:${index}">${item.imageUrl ? `<img src="${escape(item.imageUrl)}" alt="" width="96" height="96">` : '<span class="cart-gift-tier__placeholder" aria-hidden="true">🎁</span>'}</span>`).reverse().join('')}</span><span class="cart-gift-tier__title">${escape(stackFirst?.productTitle || stackFirst?.label || 'Free gift')}</span></button>
          <p class="cart-gift-tier__selection${!met && availableCandidate ? ' is-away' : ''}${receiving || stock.get(suffix(candidate?.variantId)) === false ? ' is-emphasized' : ''}" aria-live="polite">${!availableCandidate ? (stock.get(suffix(candidate?.variantId)) === false ? 'Fully Claimed' : stockLabel(candidate)) : met ? (receiving ? 'Gift Unlocked' : reward && Number(reward.tier.threshold) > Number(tier.threshold) ? 'Qualified · Gift Upgraded to Higher Tier' : 'Qualified') : `${escape(money(Math.max(0, tier.threshold - subtotal)))} away`}</p>
          ${failed ? '<button type="button" class="cart-gift-tier__retry" data-gift-retry>Retry stock check</button>' : ''}
          ${candidates.length > 1 ? `<small class="cart-gift-tier__desktop-count">${Math.max(0, candidates.indexOf(candidate)) + 1} / ${candidates.length}</small>` : ''}
        </article>`;
      }).join('');
    }).join('')}</div><p class="cart-gift-tiers__notice">*Gifts Don't Stack</p>`;
    const grid = mount.querySelector('.cart-gift-tiers__grid');
    if (grid) {
      grid.scrollLeft = previousScroll;
      if (mobile() && !mobilePositioned && !mobileTouched && !checking.size && reward && !reward.blocked) {
        const receiving = grid.querySelector('.is-receiving');
        if (receiving) {
          const target = receiving.getBoundingClientRect(), viewport = grid.getBoundingClientRect();
          grid.scrollLeft += target.left - viewport.left - (grid.clientWidth - target.width) / 2;
          mobilePositioned = true;
        }
      }
    }
    saveChoices();
    renderDialog();
    if (focusKey) mount.querySelector(`[data-focus-key="${scope.CSS.escape(focusKey)}"]`)?.focus();
  }

  async function cleanGifts({ keys, selection, adopt = false } = {}) {
    let current = await fetchCart();
    const remap = () => { if (selection) keys = new Set(selectedLines(current, selection).map(line => line.key)); };
    remap();
    if (adopt) {
      for (const line of current.items.filter(managed)) {
        const rule = config.rules?.find(item => item.id === line.properties?.[ATTR.rule]);
        const tier = rule?.tiers.find(item => item.id === line.properties?.[ATTR.tier]);
        const candidate = tier?.candidates.find(item => suffix(item.variantId) === String(line.variant_id));
        if (!candidate || line.quantity !== 1 || price(line) !== 0 || !rewardLine(config, line, {rule, tier, candidate})) continue;
        const key = `${rule.id}:${tier.id}`;
        choices.set(key, String(line.variant_id)); displays.set(key, String(line.variant_id));
      }
    }
    let retained = false, changed = false, uncertain = false;
    for (const line of current.items.filter(managed)) {
      const state = giftState(config, current, line, facts, stock, choices, rate(), keys, allows);
      if (state === 'unknown') { uncertain = true; continue; }
      if (state === 'valid' && !retained) { retained = true; continue; }
      await request('cart/change.js', { id: line.key, quantity: 0 }); changed = true;
      current = await fetchCart();
      remap();
    }
    const uncertainMessage = 'Unable to confirm your existing gift. Please retry stock checking before checkout.';
    if (uncertain) message(uncertainMessage);
    else if (document.getElementById('cart-gift-status')?.textContent === uncertainMessage) message('');
    if (changed) {
      updated = true;
      document.dispatchEvent?.(new CustomEvent('cart:refresh', { detail: { cart: current } }));
    }
    return current;
  }

  async function refresh({ clean = false, fresh = false, keys, selection } = {}) {
    let projection = document;
    cart = await fetchCart();
    const missingFacts = cart.items.some(line => !managed(line) && !external(line) &&
      !(facts || []).some(fact => String(fact.variantId) === String(line.variant_id)));
    fresh ||= !!facts && missingFacts;
    if (fresh) {
      // App-owned metafields are available only through the app embed. Re-render
      // the cart without executing its scripts to check the latest activity/revision.
      const controller = new AbortController();
      const timeout = scope.setTimeout(() => controller.abort(), 12000);
      try {
        const response = await fetch(route + 'cart?zyf_gift_runtime=1', { credentials: 'same-origin', cache: 'no-store', signal: controller.signal });
        if (!response.ok) throw new Error('Unable to refresh the gift activity. Please try again.');
        projection = new DOMParser().parseFromString(await response.text(), 'text/html');
        if (!projection.getElementById('zyf-gift-promotions-config')) throw new Error('Unable to refresh the gift activity. Please try again.');
      } catch (error) {
      if (controller.signal.aborted || error.name === 'AbortError') throw new Error('The request timed out. Please try again.');
      throw error;
    } finally { clearTimeout(timeout); }
    }
    const previousRevision = config?.revision;
    if (fresh || !facts) {
      const nextConfig = read('zyf-gift-promotions-config', { rules: [] }, projection);
      const nextFacts = read('zyf-gift-promotions-cart-context', { items: [] }, projection).items;
      if (!Array.isArray(nextConfig.rules) || !Array.isArray(nextFacts)) throw new Error('Unable to read the free gift activity. Please refresh and try again.');
      config = nextConfig; facts = nextFacts;
      lastFresh = Date.now();
      gate = null;
    }
    if (previousRevision != null && config.revision !== previousRevision) { choices.clear(); displays.clear(); }
    gate ||= scope.ZyfGiftCountry?.(config, attributes => request('cart/update.js', { attributes }));
    if (gate) cart = await gate.sync(cart);
    await refreshStock();
    cart = await cleanGifts({ adopt: clean, keys, selection });
    render();
  }

  function lock(value) {
    busy = value;
    document.documentElement.classList.toggle('cart-gift-checkout-busy', value);
    document.querySelectorAll('.cart__checkout-button,#checkout-selected').forEach(button => {
      button.setAttribute('aria-busy', String(value));
    });
  }

  const cartChanged = () => Object.assign(new Error('Your cart changed. Please try checkout again.'), { cartChanged: true });
  async function prepare(keys, note, expected) {
    const selection = expected ? [...expected.values()] : undefined;
    const remap = () => { if (selection) keys = new Set(selectedLines(cart, selection).map(line => line.key)); };
    const verify = snapshot => {
      remap();
      if (fingerprint(cart) !== snapshot) throw cartChanged();
    };
    if (scope.KateCartIsUpdating?.()) throw new Error('Your cart is updating. Please wait and try again.');
    await scope.KateCustomSizeCartNormalizer?.normalize();
    await refresh({ fresh: Date.now() - lastFresh > 15000, keys, selection });
    remap();
    if (cart.items.some(line => !managed(line) && !external(line) &&
      !facts.some(fact => String(fact.variantId) === String(line.variant_id)))) throw cartChanged();
    const snapshot = fingerprint(cart);
    // Country asset may be loaded after this adapter; resolve it only at checkout.
    gate ||= scope.ZyfGiftCountry?.(config, attributes => request('cart/update.js', { attributes }));
    if (gate) cart = await gate.sync(cart);
    if (cart.attributes?.[DEFERRED] !== '1' || (note != null && note !== (cart.note ?? ''))) {
      cart = await request('cart/update.js', { attributes: { [DEFERRED]: '1' }, ...(note == null ? {} : { note }) });
    }
    verify(snapshot);
    cart = await cleanGifts({ keys, selection });
    verify(snapshot);
    let reward = resolve(config, cart, facts, stock, choices, rate(), keys, allows);
    if (cart.items.some(line => managed(line) && giftState(config, cart, line, facts, stock, choices, rate(), keys, allows) === 'unknown')) {
      throw new Error('Unable to confirm gift stock. Please try again.');
    }
    while (reward) {
      try {
        if (!cart.items.some(line => managed(line) && giftState(config, cart, line, facts, stock, choices, rate(), keys, allows) === 'valid')) {
          await request('cart/add.js', { items: [{ id: Number(suffix(reward.candidate.variantId)), quantity: 1, properties: properties(config, reward) }] });
        }
      } catch (error) {
        // A lost response may still have added the gift. Re-read before any retry.
        const recovered = await fetchCart();
        cart = recovered;
        verify(snapshot);
        const line = recovered.items.find(item => managed(item) && rewardLine(config, item, reward));
        if (!line) {
          if (error.status !== 422) throw new Error('Unable to confirm your gift. Please try checkout again.');
          stock.set(suffix(reward.candidate.variantId), false);
          reward = resolve(config, cart, facts, stock, choices, rate(), keys, allows);
          continue;
        }
      }
      cart = await fetchCart();
      verify(snapshot);
      const gifts = cart.items.filter(managed);
      const gift = gifts.find(line => rewardLine(config, line, reward));
      if (gifts.length !== 1 || !gift || gift.quantity !== 1 || price(gift) !== 0) {
        cart = await cleanGifts({ keys, selection });
        throw new Error('The free gift discount could not be applied. Please try again or contact us.');
      }
      const currentReward = resolve(config, cart, facts, stock, choices, rate(), keys, allows);
      if (currentReward?.tier.id !== reward.tier.id || currentReward?.rule.id !== reward.rule.id) {
        cart = await cleanGifts({ keys, selection });
        throw new Error('Your gift eligibility changed. Please try checkout again.');
      }
      render();
      return { cart, gift };
    }
    // The no-gift path needs the same final read and validation as the gift path.
    cart = await fetchCart();
    verify(snapshot);
    if (resolve(config, cart, facts, stock, choices, rate(), keys, allows) || cart.items.some(managed)) {
      throw cartChanged();
    }
    const eligibleTiers = (config.rules || []).filter(allows).flatMap(rule => rule.tiers.filter(tier => amount(rule, cart, facts, rate(), keys) >= tier.threshold));
    if (eligibleTiers.length) {
      if (eligibleTiers.some(tier => tier.candidates.some(candidate => stock.get(suffix(candidate.variantId)) == null))) throw new Error('Unable to confirm gift stock. Please try again.');
      scope.alert('All eligible gifts are sold out. Your order will continue without a gift.');
    }
    render();
    return { cart, gift: null };
  }

  async function checkout(form) {
    if (busy) return;
    lock(true);
    message('Preparing your free gift…');
    try {
      for (let attempt = 0; ; attempt++) {
        try { await serial(() => prepare(undefined, document.querySelector('[name="note"]')?.value)); break; }
        catch (error) {
          if (!error.cartChanged || attempt) throw error;
          await new Promise(resolve => scope.setTimeout(resolve, 1500));
        }
      }
      if (scope.KateCartIsUpdating?.()) throw new Error('Your cart is updating. Please wait and try again.');
      form.dataset.customSizeCheckoutNormalizing = 'true';
      if (!form.querySelector('input[name="checkout"]')) {
        const input = document.createElement('input'); input.type = 'hidden'; input.name = 'checkout'; form.appendChild(input);
      }
      HTMLFormElement.prototype.submit.call(form);
    } catch (error) {
      try { cart = await cleanGifts(); render(); } catch {}
      message(error.message || 'Unable to start checkout. Please try again.'); lock(false);
    }
  }

  document.addEventListener('click', event => {
    const button = event.target.closest?.('.cart__checkout-button');
    if (button?.form) { event.preventDefault(); event.stopImmediatePropagation(); void checkout(button.form); }
    const open = event.target.closest?.('#cart-gift-tiers [data-gift-open]');
    if (open && Date.now() < suppressGiftClickUntil) { event.preventDefault(); event.stopImmediatePropagation(); return; }
    if (open && !busy) { showDialog(open); return; }
    if (event.target.closest?.('#cart-gift-tiers [data-gift-retry]')) { retryStock(); return; }
    const arrow = event.target.closest?.('#cart-gift-tiers [data-direction]');
    if (!arrow || busy) return;
    const article = arrow.closest('[data-rule]');
    const rule = config.rules.find(item => item.id === article.dataset.rule);
    const tier = rule.tiers.find(item => item.id === article.dataset.tier);
    const key = `${rule.id}:${tier.id}`;
    const index = tier.candidates.findIndex(candidate => suffix(candidate.variantId) === displays.get(key));
    const candidate = tier.candidates[(index + Number(arrow.dataset.direction) + tier.candidates.length) % tier.candidates.length];
    displays.set(key, suffix(candidate.variantId));
    const selectable = stock.get(suffix(candidate.variantId)) === true;
    const changed = selectable && choices.get(key) !== suffix(candidate.variantId);
    if (selectable) choices.set(key, suffix(candidate.variantId));
    render();
    if (changed) animateSelection(key, Number(arrow.dataset.direction));
    if (changed) void serial(async () => { cart = await cleanGifts(); render(); }).catch(error => message(error.message));
  }, true);
  // Capture on window precedes the existing document-level custom-size form handler.
  scope.addEventListener('submit', event => {
    if (event.target.matches?.('form.cart__recap') && event.target.dataset.customSizeCheckoutNormalizing !== 'true') {
      event.preventDefault(); event.stopImmediatePropagation(); void checkout(event.target);
    }
  }, true);

  scope.KateCartGifts = {
    async partial(selectedItems, originalCartId, trackingParams, keys, note) {
      if (busy) throw new Error('Checkout is already in progress.');
      lock(true);
      message('Preparing your free gift…');
      try {
        const selection = selectedItems.map((item, index) => ({ ...item,
          sku: cart.items.find(line => line.key === keys[index] || String(line.variant_id) === suffix(item.variantId))?.sku
        }));
        const prepared = await serial(() => prepare(new Set(keys), note, new Map(keys.map((key, index) => [key, selection[index]]))));
        const latestItems = selectedLines(prepared.cart, selection).map(line => ({
          variantId: String(line.variant_id), quantity: line.quantity, properties: line.properties || {},
          sellingPlanId: line.selling_plan_allocation?.selling_plan?.id ? String(line.selling_plan_allocation.selling_plan.id) : undefined
        }));
        const items = prepared.gift ? [...latestItems, { variantId: String(prepared.gift.variant_id), quantity: 1, properties: prepared.gift.properties }] : latestItems;
        const discountCodes = [...new Set([
          ...(prepared.cart.discount_codes || []).filter(item => item.applicable !== false).map(item => item.code),
          ...(prepared.cart.cart_level_discount_applications || []).filter(item => item.type === 'discount_code').map(item => item.title),
          ...prepared.cart.items.flatMap(item => (item.discount_allocations || []).map(allocation => allocation.discount_application))
            .filter(application => application?.type === 'discount_code').map(application => application.title)
        ].filter(Boolean))];
        const context = { note: note || '', attributes: { ...prepared.cart.attributes, [DEFERRED]: '1' }, discountCodes,
          expectedGift: prepared.gift ? { variantId: String(prepared.gift.variant_id), properties: prepared.gift.properties } : null };
        return await scope.ShopifyStorefrontAPI.createPartialCheckout(items, originalCartId, trackingParams, context);
      } catch (error) {
        try { cart = await cleanGifts({ keys: new Set(keys) }); render(); } catch {}
        message(error.message); throw error;
      }
      finally { lock(false); }
    }
  };

  const schedule = () => {
    if (busy || refreshPending) return;
    clearTimeout(timer);
    timer = scope.setTimeout(() => {
      refreshPending = true;
      serial(() => refresh({ fresh: true })).catch(error => message(error.message)).finally(() => { refreshPending = false; });
    }, 300);
  };
  ['cart:updated', 'cart:refresh', 'ajaxProduct:added', 'product:added'].forEach(name => document.addEventListener(name, schedule));
  scope.addEventListener('pageshow', event => { if (event.persisted) { lock(false); serial(() => refresh({ clean: true })).catch(error => message(error.message)); } });
  document.addEventListener('visibilitychange', () => { if (!document.hidden && Date.now() - lastFresh > 60000) schedule(); });
  scope.setInterval(() => { if (!document.hidden) schedule(); }, 300000);
  const ready = serial(async () => {
    config = read('zyf-gift-promotions-config', { rules: [] });
    try { const stored = JSON.parse(sessionStorage.getItem(choiceKey)); if (stored?.revision === config.revision) stored.values.forEach(([key, value]) => choices.set(key, value)); } catch {}
    await refresh({ clean: true });
    if (updated) scope.location.reload();
  });
  scope.KateCartGifts.ready = ready;
  ready.catch(error => message(error.message));
})(typeof window !== 'undefined' ? window : null);
